import React, {
  type ComponentProps,
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps } from 'react-native';

import { theme } from '@garnish/constants';

import { Button } from '../../../Button';

// ─────────────────────────────────────────────────────────────────────────────

export const ScrollspyNavItem = (props: NavItemProps) => {
  const {
    children,
    isActive = false,
    targetId,
    register,
    deregister,
    onPress,
    ...buttonProps
  } = props;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(targetId);
  }, [onPress, targetId]);

  // ─── Refs ────────────────────────────────────────────────────────────

  const ref = useRef(null);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    register(targetId, ref);

    return () => {
      deregister(targetId);
    };
  }, [targetId, register, deregister]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Button
      ref={ref}
      size="large"
      palette={isActive ? 'primary' : 'secondary'}
      style={styles.pressable}
      accessibilityState={{ selected: isActive }}
      onPress={handleOnPress}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

/**
 * This version can be used for more customizable nav items.
 */
export const ScrollspyNavItemPressable = (props: PressableNavItemProps) => {
  const {
    children,
    isActive = false,
    targetId,
    register,
    deregister,
    onPress,
    ...buttonProps
  } = props;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.(targetId);
  }, [onPress, targetId]);

  // ─── Refs ────────────────────────────────────────────────────────────

  const ref = useRef(null);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    register(targetId, ref);

    return () => {
      deregister(targetId);
    };
  }, [targetId, register, deregister]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={ref}
      accessibilityState={{ selected: isActive }}
      onPress={handleOnPress}
      {...buttonProps}
    >
      {children}
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    height: 32,
    paddingHorizontal: theme.spacing['3'],
    borderWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type NavItemProps = BaseProps & Omit<ComponentProps<typeof Button>, 'palette' | 'onPress' | 'ref'>;

type PressableNavItemProps = BaseProps & Omit<TouchableOpacityProps, 'palette' | 'onPress' | 'ref'>;

type BaseProps = {
  targetId: string;
  isActive?: boolean;
  onPress?: (targetId: string) => void;
  register: (targetId: string, targetRef: MutableRefObject<unknown>) => void;
  deregister: (targetId: string) => void;
};
